import { NgFor, NgIf } from '@angular/common';
import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectChange, MatSelectModule } from '@angular/material/select';
import { getTrackByFn, InputObservable } from '@examdojo/angular/util';
import { IconComponent } from '@examdojo/core/icon';
import { ToastService } from '@examdojo/core/toast';
import { ButtonComponent } from '@examdojo/core/button';
import { IconButtonComponent } from '@examdojo/core/icon-button';
import { IonContent, IonFooter, IonHeader, IonTitle, IonToolbar, ModalController } from '@ionic/angular/standalone';
import { base64ToFile, ImageCroppedEvent, ImageCropperComponent, ImageTransform } from 'ngx-image-cropper';
import { filter, Observable, tap } from 'rxjs';
import { ImageCropOptions, RatioOption } from './image-crop-dialog.model';

export interface ImageCropDialogData {
  file: File;
  options?: ImageCropOptions;
}

@Component({
  selector: 'dojo-image-crop-dialog',
  templateUrl: './image-crop-dialog.component.html',
  styleUrls: ['./image-crop-dialog.component.scss'],
  imports: [
    NgIf,
    NgFor,
    FormsModule,
    IconComponent,
    MatFormFieldModule,
    MatSelectModule,
    MatDividerModule,
    MatButtonModule,
    MatFormFieldModule,
    IonTitle,
    IonToolbar,
    IonContent,
    IonHeader,
    IonFooter,
    ButtonComponent,
    IconButtonComponent,
    ImageCropperComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ImageCropDialogComponent {
  constructor(
    private readonly toastService: ToastService,
    private readonly modalController: ModalController,
  ) {
    this.options$
      .pipe(
        filter(Boolean),
        tap((options) => {
          const { ratioOptions, initRatio } = options;

          if (ratioOptions) {
            this.ratioOptions = ratioOptions;
          }
          if (typeof initRatio === 'number') {
            this.ratio = initRatio;
            this.lockRatio = true;
          }
        }),
        takeUntilDestroyed(),
      )
      .subscribe();
  }

  @Input() file!: File;

  @Input()
  @InputObservable()
  options!: ImageCropOptions;
  options$!: Observable<ImageCropOptions>;

  imgHeight: number | undefined;
  imgWidth: number | undefined;

  croppedImage = '';
  showCropper = false;
  canvasRotation = 0;
  rotation = 0;
  scale = 1;
  containWithinAspectRatio = true;
  transform: ImageTransform = {};
  ratio: number = 4 / 3;
  lockRatio = false;
  ratioOptions: RatioOption[] = [
    { key: '1:1', value: 1 },
    { key: '3:4', value: 3 / 4 },
    { key: '4:3', value: 4 / 3 },
    { key: '9:16', value: 9 / 16 },
    { key: '16:9', value: 16 / 9 },
    { key: '10:16', value: 10 / 16 },
    { key: '16:10', value: 16 / 10 },
  ];

  readonly trackByValueProp = getTrackByFn<RatioOption>('key');

  imageCropped(event: ImageCroppedEvent) {
    if (!event.base64) {
      return;
    }

    this.imgHeight = event.height;
    this.imgWidth = event.width;

    this.croppedImage = event.base64;
  }

  imageLoaded() {
    this.showCropper = true;
  }

  rotateLeft() {
    this.canvasRotation--;
    this.flipAfterRotate();
  }

  rotateRight() {
    this.canvasRotation++;
    this.flipAfterRotate();
  }

  flipHorizontal() {
    this.transform = {
      ...this.transform,
      flipH: !this.transform.flipH,
    };
  }

  flipVertical() {
    this.transform = {
      ...this.transform,
      flipV: !this.transform.flipV,
    };
  }

  resetImage() {
    this.scale = 1;
    this.rotation = 0;
    this.canvasRotation = 0;
    this.transform = {};
  }

  zoomOut() {
    this.scale -= 0.1;
    this.transform = {
      ...this.transform,
      scale: this.scale,
    };
  }

  zoomIn() {
    this.scale += 0.1;
    this.transform = {
      ...this.transform,
      scale: this.scale,
    };
  }

  toggleLock() {
    this.lockRatio = !this.lockRatio;
  }

  changeRatio(change: MatSelectChange) {
    this.lockRatio = true;
    this.ratio = change.value;
  }

  toggleContainWithinAspectRatio() {
    this.containWithinAspectRatio = !this.containWithinAspectRatio;
  }

  updateRotation() {
    this.transform = {
      ...this.transform,
      rotate: this.rotation,
    };
  }

  async crop() {
    try {
      const blob = base64ToFile(this.croppedImage);
      await this.modalController.dismiss(blob);
    } catch (err: unknown) {
      console.error(err);
      this.toastService.error('Could not crop the image');
    }
  }

  async closeDialog() {
    await this.modalController.dismiss(undefined);
  }

  private flipAfterRotate() {
    const flippedH = this.transform.flipH;
    const flippedV = this.transform.flipV;
    this.transform = {
      ...this.transform,
      flipH: flippedV,
      flipV: flippedH,
    };
  }
}
